import { useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import { FaBars, FaEnvelope, FaTimes } from "react-icons/fa";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import clsx from "clsx";
import NavMenu from "./elements/menuList";
import BottomTabBar from "./elements/bottomTabBar";
import SafeImage from "@/components/SafeImage";

// Lazy-load the enquiry button so framer-motion + its SCSS don't ship in
// the initial bundle on every page that uses the header.
const SendEnquiryButton = dynamic(
  () => import("@/components/enquiry/SendEnquiryButton"),
  { ssr: false }
);

const LOGO_FULL = "/img/brand/siddhivinayak-logo.webp";
const KPIPL_LOGO = "/img/brand/kpipl-logo.webp";

const SiteHeader = () => {
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const enquiryRef = useRef(null);

  const drawerTriggerRef = useRef(null);

  const openDrawer = useCallback(() => {
    drawerTriggerRef.current = document.activeElement;
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setTimeout(() => {
      if (
        drawerTriggerRef.current &&
        typeof drawerTriggerRef.current.focus === "function"
      ) {
        drawerTriggerRef.current.focus();
      }
    }, 0);
  }, []);

  // Toggle body class for bottom-tab offset
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("has-bottom-tabs");
    return () => document.body.classList.remove("has-bottom-tabs");
  }, []);

  // Track scroll for glass effect
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    const close = () => setDrawerOpen(false);
    router.events.on("routeChangeStart", close);
    return () => router.events.off("routeChangeStart", close);
  }, [router.events]);

  // Escape to close drawer + body scroll lock
  useEffect(() => {
    if (!drawerOpen || typeof document === "undefined") return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeDrawer();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerOpen, closeDrawer]);

  return (
    <>
      <header
        className={clsx("site-navbar", scrolled && "is-sticky")}
        role="banner"
      >
        <div className="site-navbar__bar">
          <Container>
            <Row className="align-items-center g-0 site-navbar__row">
              <Col xs="auto" className="site-navbar__logo-col">
                <Link href="/" aria-label="Siddhivinayak Infra — home" className="site-logo site-logo--siddhivinayak">
                  <SafeImage src={LOGO_FULL} alt="Siddhivinayak Group" width={400} height={160} priority decoding="async" />
                </Link>
                <span className="site-logo__divider" aria-hidden="true" />
                <Link href="/" aria-label="KPIPL — home" className="site-logo site-logo--kpipl">
                  <SafeImage src={KPIPL_LOGO} alt="KPIPL" width={180} height={180} priority decoding="async" />
                </Link>
              </Col>

              <Col className="site-navbar__menu-col">
                <NavMenu onNavigate={closeDrawer} variant="bar" />
              </Col>

              <Col xs="auto" className="site-navbar__actions">
                <button
                  type="button"
                  className="site-navbar__cta"
                  onClick={() => enquiryRef.current?.open()}
                  aria-label="Open enquiry form"
                >
                  Enquiry
                </button>
                <button
                  ref={drawerTriggerRef}
                  type="button"
                  className="site-navbar__hamburger"
                  aria-label={drawerOpen ? "Close menu" : "Open menu"}
                  aria-expanded={drawerOpen}
                  aria-controls="site-navbar-drawer"
                  onClick={drawerOpen ? closeDrawer : openDrawer}
                >
                  {drawerOpen ? <FaTimes aria-hidden /> : <FaBars aria-hidden />}
                </button>
              </Col>
            </Row>
          </Container>
        </div>
      </header>

      <div
        id="site-navbar-drawer"
        className={clsx("site-drawer", drawerOpen && "is-open")}
        aria-hidden={!drawerOpen}
        inert={drawerOpen ? undefined : ""}
      >
        <div className="site-drawer__backdrop" onClick={closeDrawer} />
        <aside
          className="site-drawer__panel"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
        >
          <div className="site-drawer__head">
            <div className="site-drawer__logos">
              <Link href="/" aria-label="Siddhivinayak Infra — home" className="site-logo site-logo--siddhivinayak">
                <SafeImage src={LOGO_FULL} alt="Siddhivinayak Group" width={400} height={160} decoding="async" />
              </Link>
              <span className="site-logo__divider" aria-hidden="true" />
              <Link href="/" aria-label="KPIPL — home" className="site-logo site-logo--kpipl">
                <SafeImage src={KPIPL_LOGO} alt="KPIPL" width={180} height={180} decoding="async" />
              </Link>
            </div>
            <button
              type="button"
              className="site-drawer__close"
              aria-label="Close menu"
              onClick={closeDrawer}
            >
              <FaTimes aria-hidden />
            </button>
          </div>
          <div className="site-drawer__body">
            <NavMenu onNavigate={closeDrawer} variant="drawer" />
          </div>
        </aside>
      </div>

      <BottomTabBar onMore={openDrawer} />

      {/* Floating message action — fixed to bottom-right on every screen.
          Sits above the bottom tab bar on mobile, opens the same enquiry modal. */}
      <button
        type="button"
        className="floating-enquiry"
        onClick={() => enquiryRef.current?.open()}
        aria-label="Open enquiry form"
      >
        <span className="floating-enquiry__icon" aria-hidden="true">
          <FaEnvelope />
        </span>
      </button>

      <SendEnquiryButton
        ref={enquiryRef}
        hideTrigger
        titleOverride="Send Enquiry"
      />
    </>
  );
};

export default SiteHeader;
